/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Tooltip,
  Typography,
} from 'antd';

import { CarOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';

import Utils from '../../../../../Assets/Scripts/Utils';
import { api } from '../../../../../Services/axiosService';

import {
  isValidBrazilianLicensePlate,
  normalizeBrazilianLicensePlate,
} from './brazilianLicensePlate';
import { normalizeVehicleTypeName, REQUIRED_OCCASIONAL_VEHICLE_TYPES } from './vehicleTypeOptions';

import './DriverVehicleSelector.scss';

const { Text } = Typography;

const resolveCompanyId = (company) => {
  if (company === null || company === undefined) return null;
  if (typeof company === 'number') return company;
  if (typeof company === 'string' && company.trim() !== '' && !Number.isNaN(Number(company))) {
    return Number(company);
  }
  return company.id ?? company._id ?? company.value ?? null; // eslint-disable-line no-underscore-dangle
};

const INVALID_LICENSE_PLATE_MESSAGE = 'A placa deve estar no formato AAA-0000 ou Mercosul LLLNLNN';

const readPlateInputValue = (eventOrValue) => {
  if (eventOrValue?.target) return eventOrValue.target.value;
  return eventOrValue ?? '';
};

const normalizePlateFormValue = (eventOrValue) =>
  normalizeBrazilianLicensePlate(readPlateInputValue(eventOrValue));

const validateOccasionalLicensePlate = (_, plateValue) => {
  if (!plateValue) return Promise.resolve();
  if (isValidBrazilianLicensePlate(plateValue)) return Promise.resolve();
  return Promise.reject(new Error(INVALID_LICENSE_PLATE_MESSAGE));
};

const DECIMAL_SEPARATOR_PATTERN = /[.,]/;
const CAPACITY_SIGN_PATTERN = /[+-]/;
const SCIENTIFIC_NOTATION_PATTERN = /\d+[eE][+-]?\d+/;
const INVALID_CAPACITY_INTEGER_MESSAGE = 'A capacidade deve ser um número inteiro';
const INVALID_CAPACITY_MINIMUM_MESSAGE = 'A capacidade deve ser no mínimo 1';

const parseOccasionalCapacityInput = (rawValue) => {
  const source = String(rawValue ?? '');
  if (DECIMAL_SEPARATOR_PATTERN.test(source)) return '';
  if (CAPACITY_SIGN_PATTERN.test(source)) return '';
  if (SCIENTIFIC_NOTATION_PATTERN.test(source)) return '';
  return source.replace(/\D/g, '');
};

const validateOccasionalCapacity = (_, capacityValue) => {
  if (capacityValue === undefined || capacityValue === null || capacityValue === '') {
    return Promise.resolve();
  }

  const capacity = Number(capacityValue);
  if (!Number.isInteger(capacity)) {
    return Promise.reject(new Error(INVALID_CAPACITY_INTEGER_MESSAGE));
  }
  if (capacity < 1) {
    return Promise.reject(new Error(INVALID_CAPACITY_MINIMUM_MESSAGE));
  }
  return Promise.resolve();
};

function DriverCard({
  driver,
  driverIndex,
  availableDrivers,
  availableVehicles,
  isFieldDisabled,
  onSelectDriverInCard,
  onRemoveDriver,
  onAddVehicleToDriver,
  onRemoveVehicleFromDriver,
  onCreateDriver,
  transportCompanyId,
  onOccasionalDriverCreated,
}) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);
  const [form] = Form.useForm();

  const isEmptyCard = !driver.userId;
  const canEditVehicle = !isFieldDisabled('DriverList');
  const hasAssignedVehicle = Boolean(driver.vehicle);

  const shouldShowVehicleSection = !isEmptyCard && (canEditVehicle || hasAssignedVehicle);
  const isCreateDriverDisabled =
    isFieldDisabled('DriverList') || !onCreateDriver || !transportCompanyId;

  const fetchVehicleTypes = async () => {
    try {
      let vehicleTypes = await api
        .get(
          '/VehicleType?filters[0].Field=Status&filters[0].Condition=DIFFERENT&filters[0].Value=Inativo'
        )
        .then((res) => res.data);

      vehicleTypes = vehicleTypes || [];

      // Garante Carro, Van, Micro Ônibus e Ônibus no cadastro (mesma rotina de veículo)
      const existingNames = new Set(
        vehicleTypes.map(({ name }) => normalizeVehicleTypeName(name)).filter(Boolean)
      );

      await REQUIRED_OCCASIONAL_VEHICLE_TYPES.reduce(async (pending, requiredType) => {
        await pending;
        const normalized = normalizeVehicleTypeName(requiredType.name);
        if (existingNames.has(normalized)) {
          return undefined;
        }

        try {
          const created = await api
            .post('/VehicleType', { name: requiredType.name })
            .then((res) => res.data);
          if (created) {
            vehicleTypes.push(created);
            existingNames.add(normalized);
          }
        } catch (createError) {
          Utils.logError(createError);
        }

        return undefined;
      }, Promise.resolve());

      const preferredOrder = REQUIRED_OCCASIONAL_VEHICLE_TYPES.map(({ name }) =>
        normalizeVehicleTypeName(name)
      );

      const mapped = vehicleTypes.map(({ id: value, name: label }) => ({
        label,
        value,
        normalized: normalizeVehicleTypeName(label),
      }));

      mapped.sort((a, b) => {
        const indexA = preferredOrder.indexOf(a.normalized);
        const indexB = preferredOrder.indexOf(b.normalized);
        if (indexA === -1 && indexB === -1) return a.label.localeCompare(b.label);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });

      setVehicleTypeOptions(mapped.map(({ label, value }) => ({ label, value })));
    } catch (error) {
      Utils.logError(error);
      message.error('Oops. Algo deu errado ao buscar os tipos de veículo!');
    }
  };

  useEffect(() => {
    if (isDrawerOpen && vehicleTypeOptions.length === 0) {
      fetchVehicleTypes();
    }
  }, [isDrawerOpen]);

  const handleOpenRegisterDrawer = () => {
    if (isCreateDriverDisabled) return;
    setIsDrawerOpen(true);
  };

  const handleCloseRegisterDrawer = () => {
    form.resetFields();
    setIsDrawerOpen(false);
  };

  const handleSaveVehicle = async () => {
    try {
      const values = await form.validateFields();
      const companyId = resolveCompanyId(transportCompanyId);

      if (!companyId) {
        throw new Error('ID da transportadora não foi informado');
      }

      const selectedType = vehicleTypeOptions.find(({ value }) => value === values.tipo);
      if (!selectedType) {
        throw new Error('Tipo de veículo inválido');
      }

      setSaving(true);

      const userData = {
        name: values.nome,
        login: values.nome.toLowerCase().replace(/\s+/g, '.'),
        role: {
          _id: null, // eslint-disable-line no-underscore-dangle
          name: 'Motorista',
        },
        company: {
          _id: companyId, // eslint-disable-line no-underscore-dangle
          name: values.nome,
          companyType: 'TransportCompany',
        },
        entity: companyId,
      };

      const userResponse = await api.post('/User', userData);
      const createdUserId = userResponse.data.id;

      const capacity = parseInt(values.capacidade, 10);
      const licensePlate = normalizeBrazilianLicensePlate(values.placa);

      // Veículo ocasional: só no treinamento (driverList), sem POST /Vehicle
      const occasionalVehicle = {
        licensePlate,
        capacity,
        type: {
          id: selectedType.value,
          name: selectedType.label,
        },
        transportCompanyId: companyId,
        isOccasional: true,
      };

      const newOccasionalDriver = {
        id: createdUserId,
        nome: values.nome,
        tipo: selectedType.label,
        tipoId: selectedType.value,
        placa: licensePlate,
        capacidade: capacity,
        userId: createdUserId,
        vehicle: occasionalVehicle,
        createdAt: new Date().toISOString(),
      };

      setIsDrawerOpen(false);
      form.resetFields();
      message.success('Motorista ocasional criado com sucesso!');

      if (onOccasionalDriverCreated) {
        onOccasionalDriverCreated(newOccasionalDriver, driverIndex);
      }
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      Utils.logError(error);
      message.error(
        error?.response?.data?.title || error?.message || 'Erro ao criar motorista ocasional'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card size="small" className={`driver-card ${isEmptyCard ? 'driver-card--empty' : ''}`}>
        <div className="driver-card__content-wrapper">
          {!isFieldDisabled('DriverList') && (
            <Tooltip title="Remover motorista">
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => onRemoveDriver(driverIndex)}
                className="driver-card__remove-button"
              />
            </Tooltip>
          )}

          <div className="driver-card__header">
            <UserOutlined className="driver-card__user-icon" />
            {isEmptyCard ? (
              <div className="driver-card__driver-selector">
                <Select
                  placeholder="Selecione um motorista"
                  options={availableDrivers}
                  onChange={(driverId) => onSelectDriverInCard(driverIndex, driverId)}
                  style={{ width: '100%' }}
                  dropdownStyle={{ borderRadius: 8 }}
                  optionFilterProp="label"
                  showSearch
                  disabled={isFieldDisabled('DriverList')}
                />
                <Button
                  type="default"
                  size="small"
                  onClick={handleOpenRegisterDrawer}
                  disabled={isCreateDriverDisabled}
                  className="driver-card__new-driver-button"
                >
                  Motorista Ocasional
                </Button>
              </div>
            ) : (
              <Text className="driver-card__name">{driver.userName}</Text>
            )}
          </div>

          {shouldShowVehicleSection && (
            <div className="driver-card__content">
              <div className="driver-card__vehicle-section">
                <div className="driver-card__vehicle-label">
                  <CarOutlined className="driver-card__vehicle-icon" />
                  <Text className="driver-card__vehicle-label-text">Veículo:</Text>
                </div>

                <div className="driver-card__vehicle-content">
                  {hasAssignedVehicle ? (
                    <div className="driver-card__vehicle-info">
                      <Text className="driver-card__vehicle-text">
                        {driver.vehicle.licensePlate} - {driver.vehicle.type?.name} -{' '}
                        {driver.vehicle.capacity} lugares
                      </Text>
                      {canEditVehicle && (
                        <Tooltip title="Remover veículo">
                          <Button
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => onRemoveVehicleFromDriver(driverIndex)}
                            className="driver-card__remove-vehicle-button"
                          />
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    <div>
                      {canEditVehicle && availableVehicles.length > 0 && (
                        <Select
                          placeholder="Selecionar um veículo"
                          className="driver-card__vehicle-selector"
                          options={availableVehicles}
                          onChange={(vehicleId) => onAddVehicleToDriver(driverIndex, vehicleId)}
                          dropdownStyle={{ borderRadius: 8 }}
                          optionFilterProp="label"
                          showSearch
                        />
                      )}

                      {canEditVehicle && availableVehicles.length === 0 && (
                        <Text type="secondary" className="driver-card__vehicle-empty">
                          Nenhum veículo disponível
                        </Text>
                      )}

                      {!canEditVehicle && (
                        <Text type="secondary" className="driver-card__vehicle-empty">
                          Nenhum veículo selecionado
                        </Text>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      <Drawer
        title="Motorista Ocasional"
        placement="right"
        onClose={handleCloseRegisterDrawer}
        open={isDrawerOpen}
        width="30vw"
        bodyStyle={{
          paddingBottom: 80,
        }}
        extra={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              onClick={() => {
                form.resetFields();
              }}
              disabled={saving}
            >
              Limpar
            </Button>
            <Button
              type="primary"
              style={{ background: '#5cb85c', borderColor: '#5cb85c' }}
              onClick={handleSaveVehicle}
              loading={saving}
            >
              Salvar
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          <Row gutter={[24]}>
            <Col span={24}>
              <Form.Item
                label="Nome"
                name="nome"
                rules={[
                  {
                    required: true,
                    message: 'Campo obrigatório!',
                  },
                ]}
              >
                <Input placeholder="Digite o nome" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label="Tipo"
                name="tipo"
                rules={[
                  {
                    required: true,
                    message: 'Campo obrigatório!',
                  },
                ]}
              >
                <Select
                  placeholder="Selecione o tipo"
                  options={vehicleTypeOptions}
                  optionFilterProp="label"
                  showSearch
                  loading={vehicleTypeOptions.length === 0}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label="Placa"
                name="placa"
                getValueFromEvent={normalizePlateFormValue}
                rules={[
                  {
                    required: true,
                    message: 'Campo obrigatório!',
                  },
                  {
                    validator: validateOccasionalLicensePlate,
                  },
                ]}
              >
                <Input placeholder="Digite a placa" maxLength={8} />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label="Capacidade"
                name="capacidade"
                rules={[
                  {
                    required: true,
                    message: 'Campo obrigatório!',
                  },
                  {
                    validator: validateOccasionalCapacity,
                  },
                ]}
              >
                <InputNumber
                  placeholder="Digite a capacidade"
                  min={1}
                  precision={0}
                  parser={parseOccasionalCapacityInput}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>
    </>
  );
}

export default DriverCard;
